import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import Http from "../services/Http";
import { toastHandler } from "../../utils/utils";
import { messages } from "../../utils/messages";

// Initial state for the Assembly slice
const initialState = {
  // assemblyData: {},
  assemblyData: {
    data: [], // Initialize data as an empty array
  },
  status: "idle",
  locationId: "",
};

// Destructure the httpRequest function from Http service
const { httpRequest } = Http();

/**
 * Async thunk for fetching the list of Assembly.
 * @returns {Array} - List of Assembly.
 */
export const assemblyList = createAsyncThunk(
  "listAssembly",
  async ({ locationId, pageCount, navigate }, { rejectWithValue }) => {
    try {
      const data = await httpRequest(
        {
          method: "GET",
          url: `admin/assembly-points?location_id=${locationId}&page=${pageCount}`,
        },
        navigate
      );
      return {
        data,
        locationId,
      };
    } catch (err) {
      return rejectWithValue(err.message || messages.listAssembly.error, {
        cause: err.cause,
      });
    }
  }
);

/**
 * Async thunk for adding a new Assembly.
 * @param {Object} data - The Assembly data to be added.
 * @returns {Object} - The added Assembly data.
 */
export const addAssembly = createAsyncThunk(
  "createAssembly",
  // data into formData
  async ({ formData, navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "POST",
          body: formData,
          url: "admin/assembly-points",
        },
        navigate
      );
    } catch (err) {
      return rejectWithValue(err.message || messages.createAssembly.error, {
        cause: err.cause,
      });
    }
  }
);

/**
 * Async thunk for adding a new Assembly.
 * @param {Object} data - The Assembly data to be added.
 * @returns {Object} - The added Assembly data.
 */
export const addAssemblyPointId = createAsyncThunk(
  "createAssemblyPointId",
  async ({ formData, id, navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "POST",
          body: formData,
          url: `admin/courses/${id}/assembly-point`,
        },
        navigate
      );
    } catch (err) {
      return rejectWithValue(
        err.message || messages.createAssemblyPointId.error,
        {
          cause: err.cause,
        }
      );
    }
  }
);

/**
 * Async thunk for updating an existing Assembly.
 * @param {Object} data - The Assembly data to be updated.
 * @param {string} id - The ID of the Assembly to be updated.
 * @returns {Object} - The updated Assembly data.
 */
export const updateAssembly = createAsyncThunk(
  "editAssembly",
  async ({ formData, id, navigate }, { rejectWithValue }) => {
    try {
      await httpRequest(
        {
          method: "PUT",
          body: formData,
          url: `admin/assembly-points/${id}`,
        },
        navigate
      );
      return { formData, id };
    } catch (err) {
      return rejectWithValue(err.message || messages.editAssembly.error, {
        cause: err.cause,
      });
    }
  }
);

/**
 * Async thunk for deleting a Assembly by ID.
 * @param {string} id - The ID of the Assembly to be deleted.
 * @returns {Object} - The response from the delete request.
 */
export const deleteAssembly = createAsyncThunk(
  "deleteAssembly",
  async ({ id, locationId, navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "DELETE",
          url: `admin/assembly-points/${id}?location_id=${locationId}`,
        },
        navigate
      );
    } catch (err) {
      return rejectWithValue(err.message || messages.deleteAssembly.error, {
        cause: err.cause,
      });
    }
  }
);

/**
 * Async thunk for deleting a Assembly by ID.
 * @param {string} id - The ID of the Assembly to be deleted.
 * @returns {Object} - The response from the delete request.
 */
export const removeAssembly = createAsyncThunk(
  "removeAssembly",
  async (
    { courseId, assemblyId, locationId, navigate },
    { rejectWithValue }
  ) => {
    try {
      return await httpRequest(
        {
          method: "POST",
          body: {
            location_id: locationId,
            assembly_point_id: assemblyId,
          },
          url: `admin/courses/${courseId}/assembly-point/remove`,
        },
        navigate
      );
    } catch (err) {
      return rejectWithValue(err.message || messages.removeAssembly.error, {
        cause: err.cause,
      });
    }
  }
);

// Create a slice for Assembly-related actions and state management
const assemblySlice = createSlice({
  name: "assemblyPoint",
  initialState,
  reducers: {
    /**
     * Reducer for showing toast messages.
     * @param {Object} state - The current state.
     * @param {Object} action - The action containing payload data.
     */
    showToast: (state, action) => {
      const { success, message, navigate, navigatePath, locationState } =
        action.payload;
      toastHandler(success, message, navigate, navigatePath, locationState);
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        (action) =>
          [
            assemblyList.pending,
            addAssembly.pending,
            deleteAssembly.pending,
            removeAssembly.pending,
            updateAssembly.pending,
          ].some((type) => type.match(action)),
        (state, action) => {
          state.status = "loading";
          action.meta.arg.setLoader(true);
        }
      )
      .addMatcher(
        (action) =>
          [
            assemblyList.fulfilled,
            addAssembly.fulfilled,
            deleteAssembly.fulfilled,
            removeAssembly.fulfilled,
            updateAssembly.fulfilled,
          ].some((type) => type.match(action)),
        (state, action) => {
          state.status = "succeeded";

          if (action.type === "listAssembly/fulfilled") {
            if (state.locationId !== action.payload.locationId) {
              state.assemblyData = {
                data: [],
              };
            }

            state.locationId = action.payload.locationId;

            const existingData = state.assemblyData.data;
            const newData = action.payload.data.assemblyPoints.data;

            const mergedData = [...existingData, ...newData].reduce(
              (acc, current) => {
                if (!acc.find((item) => item.id === current.id)) {
                  acc.push(current);
                }
                return acc;
              },
              []
            );

            state.assemblyData = {
              ...action.payload.data.assemblyPoints,
              data: mergedData,
            };
          }

          // if (["listAssembly"].includes(action.type.split("/")[0])) {
          //   // state.assemblyData = action.payload.data.assemblyPoints.data;

          //   console.log('state.assemblyData',state.assemblyData);
          //   const existingData = state.assemblyData.data;
          //   console.log('state.assemblyData',state.assemblyData);

          //   console.log('existingData',existingData);
          //   const newData = action.payload.assemblyPoints.data;

          //   const mergedData = [...existingData, ...newData].reduce(
          //     (acc, current) => {
          //       if (!acc.find((item) => item.id === current.id)) {
          //         acc.push(current);
          //       }
          //       return acc;
          //     },
          //     []
          //   );

          //   state.assemblyData = {
          //     ...action.payload.assemblyPoints,
          //     data: mergedData,
          //   };
          //   console.log("state.assemblyDatasmfsfm", state.assemblyData);
          // }

          // Create
          if ("createAssembly" === action.type.split("/")[0]) {
            state.assemblyData = {
              data: [],
            };
          }

          // Update state in case of edit
          // if (action.type === "editAssembly/fulfilled") {
          //   const selectedassemblyPoint = state.assemblyData.data.find(
          //     (assemblyPoint) => assemblyPoint.id === action.payload.id
          //   );
          //   if (selectedassemblyPoint) {
          //     selectedassemblyPoint.name = action.payload.data.name;
          //   }
          // }

          // New Code
          // Update state in case of edit
          // if ("editAssembly" === action.type.split("/")[0]) {
          //   const selectedassemblyPoint = state.assemblyData.data.find(
          //     (assemblyPoint) => assemblyPoint.id === action.payload.id
          //   );
          //   if (selectedassemblyPoint) {
          //     selectedassemblyPoint.name = action.payload.data.name;
          //   }
          // }

          // Delete
          if ("deleteAssembly" === action.type.split("/")[0]) {
            state.assemblyData = {
              data: [],
            };
          }

          if ("removeAssembly" === action.type.split("/")[0]) {
            state.assemblyData = {
              data: [],
            };
          }

          action.meta.arg.setLoader(false);
          // Show toast only in case of add, edit, delete Assembly
          if (
            [
              "createAssembly/fulfilled",
              "createAssemblyPointId/fulfilled",
              "editAssembly/fulfilled",
              "deleteAssembly/fulfilled",
              "removeAssembly/fulfilled",
            ].includes(action.type)
          ) {
            assemblySlice.caseReducers.showToast(state, {
              payload: {
                success: true,
                message:
                  action?.payload?.message ||
                  messages[action.type.split("/")[0]]?.success,
              },
            });
          }
        }
      )
      .addMatcher(
        (action) =>
          [
            assemblyList.rejected,
            addAssembly.rejected,
            deleteAssembly.rejected,
            removeAssembly.rejected,
            updateAssembly.rejected,
          ].some((type) => type.match(action)),
        (state, action) => {
          state.status = "failed";
          action.meta.arg.setLoader(false);
          assemblySlice.caseReducers.showToast(state, {
            payload: {
              success: false,
              message: action.payload,
            },
          });
        }
      );
  },
});

export default assemblySlice.reducer;
